'use client'

import * as React from 'react'
import { getCookie, setCookie } from 'utils/dom'
import invariant from 'tiny-invariant'

import { initAmplitude, setAmplitudeUser, getAmplitudeUser, getAmplitudeSessionId } from './utils'
import { useIsLoggedInQuery } from '../hooks/use-is-logged-in-query'
import { useUserIdQuery } from '../hooks/use-user-id'
import { AMPLITUDE_DEVICE_ID_COOKIE_NAME, AMPLITUDE_SESSION_ID_COOKIE_NAME } from './constants'

// eslint-disable-next-line no-barrel-files/no-barrel-files
// oxlint-disable-next-line cf-web/no-barrel-file
export { AMPLITUDE_DEVICE_ID_COOKIE_NAME, AMPLITUDE_SESSION_ID_COOKIE_NAME }

import { getCookieDomain } from 'utils/env'

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
invariant(cookieDomain, 'Cookie domain is not defined inside analytics/amplitude')

export const Amplitude = React.memo(() => {
  const isLoggedInQuery = useIsLoggedInQuery()
  const userIdQuery = useUserIdQuery()
  const amplitudeCookieDeviceId = getCookie(AMPLITUDE_DEVICE_ID_COOKIE_NAME)
  const amplitudeCookieSessionId = getCookie(AMPLITUDE_SESSION_ID_COOKIE_NAME)

  React.useEffect(() => {
    let cancelled = false
    void (async () => {
      await initAmplitude(amplitudeCookieDeviceId, amplitudeCookieSessionId)
      if (cancelled) {
        return
      }
      const amplitudeSessionId = getAmplitudeSessionId()
      if (!amplitudeSessionId) {
        return
      }
      const asString = String(amplitudeSessionId)
      if (asString !== amplitudeCookieSessionId) {
        setCookie({
          name: AMPLITUDE_SESSION_ID_COOKIE_NAME,
          value: asString,
          expiresIn: 30 * 60 * 1000, //30 minutes
          domain: getCookieDomain(cookieDomain),
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [amplitudeCookieDeviceId, amplitudeCookieSessionId])

  React.useEffect(() => {
    if (isLoggedInQuery.isFetching || userIdQuery.isFetching) {
      return
    }

    if (isLoggedInQuery.data && userIdQuery.data) {
      const user = `user-${userIdQuery.data}`
      const amplitudeUser = getAmplitudeUser()

      if (amplitudeUser !== user) {
        setAmplitudeUser(user)
      }
    } else {
      setAmplitudeUser(null)
    }
  }, [isLoggedInQuery.isFetching, isLoggedInQuery.data, userIdQuery.isFetching, userIdQuery.data])

  return null
})

Amplitude.displayName = 'Amplitude'
